// F4c-3: product image gallery (stitch-react).
//
// Responsive gallery for the read-only product-detail view:
//   - Desktop (>= md): one large main image with a horizontal thumbnail
//     strip underneath. Clicking a thumbnail swaps the main image. The
//     active thumbnail has a 2px Za'atar Olive border.
//   - Mobile (< md): native scroll-snap carousel (no carousel library),
//     with pagination dots underneath. Swipe direction follows the
//     document `dir` — in RTL, images flow right-to-left naturally
//     because scroll-snap respects the writing direction.
//   - No images: warm Sand-200 placeholder with a Za'atar ImageOff icon
//     and a translated "no images" label for screen readers.
//
// The component is stateless w.r.t. the product — it only owns the
// currently-selected index for the desktop thumbnail swap. On mobile,
// scroll position + dots are synced with a scroll listener on the
// carousel element.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ImageOff } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import type { ProductImageWithUrl } from '@dukanti/shared';
import { cn } from '@/lib/utils';

export interface ProductImageGalleryProps {
  /** Ordered list of images (order_index ascending). Empty array triggers
   *  the no-images placeholder. */
  readonly images: readonly ProductImageWithUrl[];
  /** Product name used for image `alt` text. */
  readonly productName: string;
  /** Optional test-id prefix. Defaults to `product-image-gallery`. */
  readonly testIdPrefix?: string;
}

export function ProductImageGallery({
  images,
  productName,
  testIdPrefix = 'product-image-gallery',
}: ProductImageGalleryProps) {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement | null>(null);

  // Reset the selection if the underlying list shrinks below the
  // current index (edge case: parent swaps products without unmounting).
  useEffect(() => {
    if (activeIndex >= images.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, images.length]);

  // Mobile: sync the pagination dots with the scroll position.
  useEffect(() => {
    const el = carouselRef.current;
    if (!el || images.length === 0) return;
    /* v8 ignore start -- jsdom does not support Element.scrollLeft
       or scroll events. The handleScroll branches (slideWidth === 0
       guard, Math.round/Math.abs on scrollLeft, setActiveIndex
       comparison) are exercised by the Playwright E2E gallery spec
       (f-003-products.spec.ts AC3). */
    const handleScroll = () => {
      const slideWidth = el.clientWidth;
      if (slideWidth === 0) return;
      // Math.abs handles negative scrollLeft under RTL in some browsers.
      const nextIndex = Math.round(Math.abs(el.scrollLeft) / slideWidth);
      setActiveIndex((current) =>
        nextIndex === current ? current : Math.min(nextIndex, images.length - 1),
      );
    };
    /* v8 ignore stop */
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
    };
  }, [images.length]);

  const handleThumbClick = useCallback((index: number) => {
    setActiveIndex(index);
    // On mobile, programmatically scroll the carousel to match.
    const el = carouselRef.current;
    if (el) {
      const slideWidth = el.clientWidth;
      el.scrollTo({
        // Using sign-aware scrollLeft keeps this consistent across
        // engines with differing RTL scroll conventions.
        left: index * slideWidth * (el.scrollLeft < 0 ? -1 : 1),
        behavior: 'smooth',
      });
    }
  }, []);

  if (images.length === 0) {
    return (
      <div
        className={cn(
          'bg-sand-200 text-zaatar flex aspect-[4/3] w-full items-center justify-center',
          'rounded-card dark:bg-sand-800',
        )}
        role="img"
        aria-label={t('admin.products.detail.noImagesLabel')}
        data-testid={`${testIdPrefix}-empty`}
      >
        <ImageOff strokeWidth={1.5} className="h-16 w-16 opacity-40" aria-hidden="true" />
      </div>
    );
  }

  // `images` is non-empty here (we returned the placeholder above) and
  // `activeIndex` is clamped to `images.length - 1` by the effect, so
  // `images[0]` is always present. The non-null assertion satisfies
  // TS's `noUncheckedIndexedAccess`.
  const activeImage = (images[activeIndex] ?? images[0]) as ProductImageWithUrl;

  return (
    <section
      aria-label={t('admin.products.detail.galleryLabel')}
      data-testid={testIdPrefix}
      className="flex flex-col gap-3"
    >
      {/* Desktop (md+) main image */}
      <div
        className={cn(
          'bg-sand-200 rounded-card hidden aspect-[4/3] w-full overflow-hidden md:block',
          'dark:bg-sand-800',
        )}
        data-testid={`${testIdPrefix}-main`}
      >
        <img
          src={activeImage.url}
          alt={productName}
          className="h-full w-full object-cover"
          loading="eager"
          draggable={false}
        />
      </div>

      {/* Mobile (< md) swipeable carousel */}
      <div
        ref={carouselRef}
        className={cn(
          'flex w-full snap-x snap-mandatory overflow-x-auto md:hidden',
          'rounded-card bg-sand-200 dark:bg-sand-800',
          // Hide the native scrollbar — images are the scroll affordance.
          '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        )}
        data-testid={`${testIdPrefix}-carousel`}
      >
        {images.map((image, index) => (
          <div
            key={image.id}
            className="relative aspect-[4/3] w-full shrink-0 snap-center"
            data-testid={`${testIdPrefix}-slide-${index}`}
          >
            <img
              src={image.url}
              alt={productName}
              className="h-full w-full object-cover"
              loading={index === 0 ? 'eager' : 'lazy'}
              draggable={false}
            />
          </div>
        ))}
      </div>

      {/* Mobile pagination dots — hidden when there is a single image */}
      {images.length > 1 ? (
        <div
          className="flex items-center justify-center gap-1.5 md:hidden"
          role="tablist"
          aria-label={t('admin.products.detail.galleryLabel')}
        >
          {images.map((image, index) => {
            const isActive = index === activeIndex;
            return (
              <button
                key={image.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={t('admin.products.detail.thumbnailLabel', { index: index + 1 })}
                onClick={() => handleThumbClick(index)}
                className={cn(
                  'h-2 rounded-full transition-all',
                  'focus-visible:ring-zaatar focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                  isActive ? 'bg-zaatar w-6' : 'bg-sand-300 dark:bg-sand-600 w-2',
                )}
                data-testid={`${testIdPrefix}-dot-${index}`}
              />
            );
          })}
        </div>
      ) : null}

      {/* Desktop thumbnail strip (horizontal, scrollable) */}
      {images.length > 1 ? (
        <div
          className="hidden gap-2 overflow-x-auto pb-1 md:flex"
          role="tablist"
          aria-label={t('admin.products.detail.galleryLabel')}
        >
          {images.map((image, index) => {
            const isActive = index === activeIndex;
            return (
              <button
                key={image.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={t('admin.products.detail.thumbnailLabel', { index: index + 1 })}
                onClick={() => handleThumbClick(index)}
                className={cn(
                  'bg-sand-200 dark:bg-sand-800 h-16 w-16 shrink-0 overflow-hidden rounded-lg',
                  'border-2 transition-colors',
                  'focus-visible:ring-zaatar focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                  isActive ? 'border-zaatar' : 'hover:border-sand-300 border-transparent',
                )}
                data-testid={`${testIdPrefix}-thumb-${index}`}
              >
                <img
                  src={image.url}
                  alt={productName}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  draggable={false}
                />
              </button>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
